import React from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Shield, Lock, Database, UserCheck, Cookie, Mail } from "lucide-react";

export default function Privacidad() {
  return (
    <div className="App" data-testid="privacidad-page">
      <Navbar />
      <section className="bg-zinc-50 border-b border-zinc-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <span className="overline text-amber-700">Privacidad</span>
          <h1 className="mt-3 font-display text-4xl lg:text-5xl font-extrabold tracking-tighter">
            Política de privacidad
          </h1>
          <p className="mt-4 text-slate-600 leading-relaxed max-w-2xl">
            Esta política describe qué datos personales recolecta DerechoLlave, con qué fin, cómo los
            resguardamos y qué derechos tienes sobre ellos, en el marco de la legislación chilena
            (Ley N° 19.628 sobre protección de la vida privada).
          </p>
          <p className="mt-2 text-xs text-slate-400">
            Última actualización: {new Date().toLocaleDateString("es-CL", { year: "numeric", month: "long" })}
          </p>
        </div>
      </section>

      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 space-y-12">

        <Block n="1" icon={<UserCheck className="w-5 h-5" />} title="Datos que recolectamos">
          <p>Recolectamos únicamente los datos necesarios para operar la plataforma:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li><strong>Cuenta:</strong> nombre completo, email, teléfono (opcional), contraseña cifrada con bcrypt y rol (vendedor / inversionista / administrador).</li>
            <li><strong>Publicaciones (vendedor):</strong> datos del negocio, métricas operativas, fotos y documentos privados (balance, contratos, patentes).</li>
            <li><strong>Interacciones (inversionista):</strong> favoritos, solicitudes de información / oferta / visita, mensajes intermediados, aceptaciones de protocolos.</li>
            <li><strong>Técnicos:</strong> dirección IP, tipo de navegador, eventos de auditoría (login, edición, eliminación) para seguridad y trazabilidad.</li>
          </ul>
        </Block>

        <Block n="2" icon={<Database className="w-5 h-5" />} title="Finalidad del tratamiento">
          <ul className="list-disc pl-5 space-y-2">
            <li>Habilitar el funcionamiento del marketplace (publicar, buscar, contactar mediante intermediación).</li>
            <li>Proteger la confidencialidad de las partes y la comisión de intermediación.</li>
            <li>Enviar comunicaciones operativas: confirmación de cuenta, recuperación de contraseña, notificaciones de solicitudes y avances del proceso.</li>
            <li>Cumplir obligaciones legales, contables y tributarias.</li>
            <li>Mejorar el servicio mediante métricas agregadas y anónimas.</li>
          </ul>
        </Block>

        <Block n="3" icon={<Lock className="w-5 h-5" />} title="Seguridad y resguardo">
          <p>
            Las contraseñas se almacenan con hash <strong>bcrypt</strong>. Las comunicaciones con el
            servidor viajan cifradas por <strong>HTTPS/TLS</strong>. El acceso a documentos privados
            (balance, contratos, patentes) está restringido: cada solicitud queda registrada y
            requiere autorización explícita.
          </p>
          <p>
            Mantenemos registros de auditoría inmutables para detectar accesos indebidos. En caso de
            incidente, te notificaremos en cuanto sea razonablemente posible.
          </p>
        </Block>

        <Block n="4" icon={<Shield className="w-5 h-5" />} title="Compartición con terceros">
          <p>
            <strong>No vendemos ni alquilamos tus datos.</strong> Los datos personales solo se
            comparten con:
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li><strong>Proveedores estrictamente necesarios:</strong> Supabase (base de datos), Resend (envío de correos transaccionales) y un proveedor de almacenamiento de archivos. Todos con cláusulas de confidencialidad.</li>
            <li><strong>Autoridades:</strong> ante requerimiento legal formal de un organismo competente (Ministerio Público, SII, tribunales).</li>
            <li><strong>La contraparte de tu operación</strong> solo cuando autorizas expresamente el avance a la etapa correspondiente (visita acompañada o cierre).</li>
          </ul>
        </Block>

        <Block n="5" icon={<Cookie className="w-5 h-5" />} title="Cookies y almacenamiento local">
          <p>
            Usamos almacenamiento local del navegador (<code className="bg-zinc-100 px-1 rounded text-xs">localStorage</code>)
            para mantener tu sesión iniciada (token JWT). No utilizamos cookies de seguimiento publicitario
            ni compartimos tu actividad con redes de anuncios.
          </p>
        </Block>

        <Block n="6" icon={<UserCheck className="w-5 h-5" />} title="Tus derechos (Ley 19.628)">
          <p>Como titular de tus datos, puedes ejercer en cualquier momento los derechos de:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li><strong>Acceso:</strong> conocer qué datos tuyos tratamos.</li>
            <li><strong>Rectificación:</strong> corregir información inexacta o desactualizada.</li>
            <li><strong>Cancelación:</strong> solicitar la eliminación cuando no exista obligación legal de conservación.</li>
            <li><strong>Oposición:</strong> oponerte al tratamiento por motivos justificados.</li>
            <li><strong>Portabilidad:</strong> obtener tus datos en formato estructurado.</li>
          </ul>
          <p>
            Para ejercerlos escríbenos a{" "}
            <a href="mailto:privacidad@derechollave.cl" className="underline font-semibold">privacidad@derechollave.cl</a>.
            Te responderemos dentro de 10 días hábiles.
          </p>
        </Block>

        <Block n="7" icon={<Database className="w-5 h-5" />} title="Conservación de los datos">
          <p>
            Conservamos tus datos mientras tu cuenta esté activa. Si solicitas la eliminación, anonimizaremos
            tu información en un plazo máximo de <strong>30 días</strong>, salvo los registros que la ley nos
            obligue a conservar (auditoría, contabilidad, prevención de fraude).
          </p>
        </Block>

        <Block n="8" icon={<Mail className="w-5 h-5" />} title="Contacto del responsable">
          <p>
            Responsable del tratamiento: <strong>DerechoLlave</strong>, marketplace de negocios funcionando en Chile.
          </p>
          <p>
            Correo de contacto:{" "}
            <a href="mailto:privacidad@derechollave.cl" className="underline">privacidad@derechollave.cl</a>
          </p>
        </Block>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
          <p className="text-sm text-amber-900 leading-relaxed">
            <strong>Vigencia:</strong> Esta política puede actualizarse. La versión vigente es siempre la
            publicada en esta página. Si introducimos cambios significativos te avisaremos por correo
            antes de su entrada en vigencia.
          </p>
        </div>

        <div className="text-center pt-8">
          <Link to="/" className="text-sm font-semibold hover:underline">← Volver al inicio</Link>
        </div>
      </article>

      <Footer />
    </div>
  );
}

function Block({ n, icon, title, children, highlight = false }) {
  return (
    <section data-testid={`privacy-block-${n}`} className={`${highlight ? "border-2 border-amber-300 bg-amber-50/40" : "border border-zinc-200 bg-white"} rounded-xl p-7`}>
      <div className="flex items-start gap-4">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${highlight ? "bg-amber-600 text-white" : "bg-slate-900 text-white"}`}>
          {icon}
        </div>
        <div className="flex-1">
          <p className="overline text-slate-500">Sección {n}</p>
          <h2 className="mt-1 font-display text-2xl font-bold tracking-tight">{title}</h2>
          <div className="mt-4 space-y-3 text-slate-700 leading-relaxed">{children}</div>
        </div>
      </div>
    </section>
  );
}
